"use client";

import * as React from "react";
import { cn } from "../../../lib/cn";
import { Button } from "../../../primitives/Button";
import { Input } from "../../../primitives/Input";
import { Textarea } from "../../../primitives/Textarea";
import { Label } from "../../../primitives/Label";
import { PanelHeader, PanelFooter } from "./PanelHeader";

/**
 * PasteTextPanel — paste raw text into the vault. LIVE panel.
 *
 * Ported from the donor's `PasteTextView.tsx`. Keeps the File Name input, the
 * content textarea, and the **live character counter**
 * (`text.length.toLocaleString()` + "characters"). The submit CTA is disabled
 * until the content is non-empty (`!text.trim()`), exactly as the donor.
 * Navy hex / `bg-white` / `gray-*` become brand tokens via the `<Input>` /
 * `<Textarea>` / `<Label>` primitives.
 *
 * Controlled: the parent owns `text` + `fileName`. The auto-filename behavior
 * (derive a name from the first 30 chars of pasted text) lives in the parent
 * via `autoFilenameFromText` so this panel stays presentational.
 */
export interface PasteTextPanelProps {
  /** Content textarea value (controlled). */
  text: string;
  /** Content change handler. */
  onTextChange: (text: string) => void;
  /** File-name input value (controlled). */
  fileName: string;
  /** File-name change handler. */
  onFileNameChange: (name: string) => void;
  /** Back-to-grid handler. */
  onBack: () => void;
  /** Submit handler — fired only when content is non-empty. */
  onSubmit: () => void;
  /** Submit button label. Default "Add to vault". */
  submitLabel?: string;
  className?: string;
}

export const PasteTextPanel: React.FC<PasteTextPanelProps> = ({
  text,
  onTextChange,
  fileName,
  onFileNameChange,
  onBack,
  onSubmit,
  submitLabel = "Add to vault",
  className,
}) => {
  const reactId = React.useId();
  const nameId = `${reactId}-name`;
  const contentId = `${reactId}-content`;
  const disabled = !text.trim();

  return (
    <div className={cn("flex h-full flex-col gap-4", className)}>
      <PanelHeader
        title="Paste text"
        description="Add text content directly to your vault"
        onBack={onBack}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div>
          <Label htmlFor={nameId} className="mb-1.5 block">
            File name
          </Label>
          <Input
            id={nameId}
            placeholder="Enter file name..."
            value={fileName}
            onChange={(e) => onFileNameChange(e.target.value)}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-1.5 flex items-center justify-between">
            <Label htmlFor={contentId}>Content</Label>
            <span className="text-xs text-muted-foreground">
              {text.length.toLocaleString()} characters
            </span>
          </div>
          <Textarea
            id={contentId}
            placeholder="Paste or type your content here..."
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            className="min-h-[160px] flex-1 resize-none"
          />
        </div>
      </div>

      <PanelFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button type="button" disabled={disabled} onClick={onSubmit}>
          {submitLabel}
        </Button>
      </PanelFooter>
    </div>
  );
};
PasteTextPanel.displayName = "PasteTextPanel";
